import { useEffect, useRef } from "react";

import { ScaleControl } from "mapbox-gl";
import type mapboxgl from "mapbox-gl";
import { UnitSystem } from "types/units";

import { useLengthConversion } from "hooks/useUnitConversion";

export enum MapboxScaleUnit {
  Metric = "metric",
  Imperial = "imperial",
  Nautical = "nautical"
}

function getMapboxScaleUnit(unitSystem: UnitSystem): MapboxScaleUnit {
  switch (unitSystem) {
    case UnitSystem.Imperial:
      return MapboxScaleUnit.Imperial;
    case UnitSystem.SI:
    case UnitSystem.Hybrid:
    default:
      return MapboxScaleUnit.Metric;
  }
}

export function useMapScaleControl(mapbox: mapboxgl.Map | null) {
  const scaleControlRef = useRef<ScaleControl | null>(null);
  const { unitType } = useLengthConversion();
  const unitTypeRef = useRef(unitType);

  useEffect(() => {
    unitTypeRef.current = unitType;
  }, [unitType]);

  useEffect(() => {
    if (!mapbox) return;

    if (!scaleControlRef.current) {
      scaleControlRef.current = new ScaleControl();
      mapbox.addControl(scaleControlRef.current);

      const scaleUnit = getMapboxScaleUnit(unitTypeRef.current);
      scaleControlRef.current.setUnit(scaleUnit);
    }

    return () => {
      // Cleanup on unmount
      if (scaleControlRef.current && mapbox) {
        try {
          mapbox.removeControl(scaleControlRef.current);
        } catch {
          // Control may already be removed
        }
        scaleControlRef.current = null;
      }
    };
  }, [mapbox]);

  useEffect(() => {
    if (scaleControlRef.current) {
      const scaleUnit = getMapboxScaleUnit(unitType);
      scaleControlRef.current.setUnit(scaleUnit);
    }
  }, [unitType]);

  return scaleControlRef;
}
